import { useState } from 'react';
import * as Yup from 'yup';
import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useReactHookForm } from "../../hooks/useReactHookForm";
import { usersApiKeyStore } from '../../api/users-api-key-store';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { FormAutoCompleteSelectDynamic } from '../query-builder/hooks-form-querybuilder/form-autocomplete-select.dynamic.component';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';
import { planReviewApiKeyStore } from '../../api/plan-review-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';


export const assignedToFormSchema = Yup.object().shape({
    userRole: Yup.object()
        .shape({
            roleID: Yup.string()
                .nullable()
                .required('Role is required')
                .test('not-zero', 'Role is required', (value) => value !== '' && value !== '0'),
        })
        .required('Role is required'),
});

const styling = {
    dialogTitleStyle: { padding: '16px 24px 0' },
    contentPadding: { paddingTop: '30px' },
};

interface UserRoleDialog {
    roleID: number
    onSubmit: (userRole: any) => void;
    onClosed: () => void;
}

export const UserRoleDialog = ({ roleID, onSubmit, onClosed }: UserRoleDialog) => {

    const form = useReactHookForm({
        defaultValues: {
            userRole: Number(roleID) ? { roleID: Number(roleID), roleName: '' } : null
        },
        yupSchema: assignedToFormSchema
    });

    const [isDisabled, setDisabled] = useState<boolean>(!roleID);

    const { isLoading, mutateAsync } = useSaveData<any>(planReviewApiKeyStore.savePlanReviewDashboardRole());

    const submitHanlder = async (value: any) => {
        const formData = { roleID: value?.userRole?.roleID };
        await mutateAsync({ data: formData });
        onSubmit({
            roleID: value?.userRole?.roleID,
            roleCode: value?.userRole?.roleCode,
            roleName: value?.userRole?.roleName
        });
    };

    const handleRoleChange = (value: any) => { setDisabled(value ? false : true) }

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Dialog
            open={true}
            fullWidth={true}
            maxWidth={'sm'}
            // onClose={handleClose}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
        >
            <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
                User Role
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClosed}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 9,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Tooltip title={<Typography variant="body1">Close</Typography>}>
                    <CloseIcon />
                </Tooltip>
            </IconButton>
            <DialogContent dividers style={styling.contentPadding} className="popupDilog">
                <ReactHookForm formName="USER_ROLE_FORM" onSubmit={submitHanlder} formContextProps={form}>
                    <Grid container spacing={2}>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <FormAutoCompleteSelectDynamic
                                name="userRole"
                                label="Role"
                                onChange={handleRoleChange}
                                getQueryKeyConfig={() => usersApiKeyStore.getAssignedRoles()}
                                optionsValueKey="roleID"
                                optionsLabelKey="roleName"
                            />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <div className="spaceBetween">
                                <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                                    Cancel
                                </Button>
                                <Button type='submit' variant="contained" disabled={isDisabled || isLoading} style={{ width: '130px' }}>
                                    Save
                                </Button>
                            </div>
                        </Grid>
                    </Grid>
                </ReactHookForm>
            </DialogContent>
        </Dialog>
    );
}